import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Clock, CheckCircle } from 'lucide-react';

export function Contact() {
  useEffect(() => {
    // Load Zoho analytics script
    const script = document.createElement('script');
    script.id = 'wf_anal';
    script.src = 'https://crm.zohopublic.in/crm/WebFormAnalyticsServeServlet?rid=d7643ade7e087b51767c2061c5ab2e9316599efd5de35dfa87904ad9380280a35e131cf5af9765b3f3c07c001edb402egidb73a94a3211d51ed92a1aaab54ff3097ff555f762a313e18b967aeacf3199e94gidf12fbadf3a23fed677723e955443e00bf37a464868ed90063b62cacdcd6c8653gid10db358171d86b7beb07e9994a02697da06d5d2eb436e09922189348028fb64b&tw=7c439e87ec0767997dcf57f5013d2e616a62ad68de1950accfefd4ba543d4274';
    script.async = true;
    
    // Check if script already exists
    if (!document.getElementById('wf_anal')) {
      document.head.appendChild(script);
    }

    // Add the Zoho validation functions to window object
    (window as any).validateEmail961908000000514006 = () => {
      const form = document.forms['WebToLeads961908000000514006' as any] as HTMLFormElement;
      if (!form) return true;
      
      const emailFld = form.querySelectorAll('[ftype=email]') as NodeListOf<HTMLInputElement>;
      for (let i = 0; i < emailFld.length; i++) {
        const emailVal = emailFld[i].value;
        if ((emailVal.replace(/^\s+|\s+$/g, '')).length !== 0) {
          const atpos = emailVal.indexOf('@');
          const dotpos = emailVal.lastIndexOf('.');
          if (atpos < 1 || dotpos < atpos + 2 || dotpos + 2 >= emailVal.length) {
            alert('Please enter a valid email address.');
            emailFld[i].focus();
            return false;
          }
        }
      }
      return true;
    };

    (window as any).checkMandatory961908000000514006 = () => {
      const mndFileds = ['Company', 'Last Name', 'Email'];
      const fldLangVal = ['Company', 'Last Name', 'Email'];
      
      const form = document.forms['WebToLeads961908000000514006' as any] as HTMLFormElement;
      if (!form) return false;
      
      for (let i = 0; i < mndFileds.length; i++) {
        const fieldObj = form[mndFileds[i] as any] as HTMLInputElement;
        if (fieldObj) {
          if (((fieldObj.value).replace(/^\s+|\s+$/g, '')).length === 0) {
            if (fieldObj.type === 'file') {
              alert('Please select a file to upload.');
              fieldObj.focus();
              return false;
            }
            alert(fldLangVal[i] + ' cannot be empty.');
            fieldObj.focus();
            return false;
          }
          else if (fieldObj.nodeName === 'SELECT') {
            const selectField = fieldObj as unknown as HTMLSelectElement;
            if (selectField.options[selectField.selectedIndex].value === '-None-') {
              alert(fldLangVal[i] + ' cannot be none.');
              fieldObj.focus();
              return false;
            }
          }
          else if (fieldObj.type === 'checkbox') {
            if (!fieldObj.checked) {
              alert('Please accept ' + fldLangVal[i]);
              fieldObj.focus();
              return false;
            }
          }
        }
      }
      
      if (!(window as any).validateEmail961908000000514006()) {
        return false;
      }
      
      return true;
    };

    // Set dynamic return URL based on current page
    const setDynamicReturnURL = () => {
      const returnURLFields = document.querySelectorAll('input[name="returnURL"]') as NodeListOf<HTMLInputElement>;
      returnURLFields.forEach(field => {
        field.value = window.location.href;
      });
    };

    // Set return URL when component mounts
    setTimeout(setDynamicReturnURL, 100);

    return () => {
      // Cleanup function to remove script if component unmounts
      const existingScript = document.getElementById('wf_anal');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <section id="contact" className="py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <Badge className="mb-4 bg-blue-100 text-blue-800">Get In Touch</Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
            Get a free consultation and discover how Zoho CRM, Zoho One, and automation solutions can streamline 
            your operations and accelerate growth.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Zoho CRM Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg lg:text-xl">Get Free Consultation</CardTitle>
              <p className="text-gray-600 text-sm">
                Transform your business with Zoho. Start today!
              </p>
            </CardHeader>
            <CardContent>
              {/* Benefits List */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Free assessment &amp; custom roadmap</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">ROI analysis &amp; implementation plan</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Dedicated implementation support</span>
                </div>
              </div>

              {/* Zoho CRM Form - Using native HTML form without React interference */}
              <div className="zoho-form-container">
                <div id="crmWebToEntityForm" className="zcwf_lblLeft crmWebToEntityForm" style={{backgroundColor: 'white', color: 'black', maxWidth: '600px'}}>
                  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                  <meta httpEquiv="content-type" content="text/html;charset=UTF-8" />
                  
                  <form 
                    id="webform961908000000514006" 
                    action="https://crm.zoho.in/crm/WebToLeadForm" 
                    name="WebToLeads961908000000514006" 
                    method="POST" 
                    acceptCharset="UTF-8"
                    onSubmit={(e) => {
                      const isValid = (window as any).checkMandatory961908000000514006();
                      if (!isValid) {
                        e.preventDefault();
                      }
                    }}
                  >
                    
                    {/* Required Hidden Fields */}
                    <input type="text" style={{display:'none'}} name="xnQsjsdp" defaultValue="f1fdae68f7843ae404a1c9bdb17b3cf88fe7340b7bf4681581a76404f5cc98f2" readOnly />
                    <input type="hidden" name="zc_gad" id="zc_gad" defaultValue="" />
                    <input type="text" style={{display:'none'}} name="xmIwtLD" defaultValue="63416910ce071231250ab4789f8cc7f7c2a091d722bcad1bd410503b28140914982203cbcbc7deaa630726fb120d11cc" readOnly />
                    <input type="text" style={{display:'none'}} name="actionType" defaultValue="TGVhZHM=" readOnly />
                    <input type="text" style={{display:'none'}} name="returnURL" defaultValue="" readOnly />
                    
                    {/* Hidden Lead Source Field */}
                    <div style={{display: 'none'}}>
                      <select id="Lead_Source" name="Lead Source" defaultValue="Website">
                        <option value="-None-">-None-</option>
                        <option value="Advertisement">Advertisement</option>
                        <option value="Website">Website</option>
                        <option value="Cold Call">Cold Call</option>
                        <option value="Employee Referral">Employee Referral</option>
                        <option value="External Referral">External Referral</option>
                        <option value="Online Store">Online Store</option>
                        <option value="Partner">Partner</option>
                        <option value="Public Relations">Public Relations</option>
                        <option value="Sales Email Alias">Sales Email Alias</option>
                        <option value="Seminar Partner">Seminar Partner</option>
                        <option value="Internal Seminar">Internal Seminar</option>
                        <option value="Trade Show">Trade Show</option>
                        <option value="Web Download">Web Download</option>
                        <option value="Web Research">Web Research</option>
                        <option value="Chat">Chat</option>
                        <option value="X (Twitter)">X (Twitter)</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Linkedin">Linkedin</option>
                      </select>
                    </div>
                    
                    <input type="text" style={{display: 'none'}} name="aG9uZXlwb3Q" defaultValue="" />

                    {/* Name Fields Row */}
                    <div className="zcwf_row">
                      <div className="flex gap-2">
                        {/* First Name */}
                        <div className="flex-1">
                          <div className="zcwf_col_lab">
                            <label htmlFor="First_Name" className="text-sm font-medium text-gray-700 mb-1 block">
                              First Name
                            </label>
                          </div>
                          <div className="zcwf_col_fld">
                            <input 
                              type="text" 
                              id="First_Name" 
                              name="First Name" 
                              maxLength={40}
                              className="form-input w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="First name"
                            />
                          </div>
                        </div>

                        {/* Last Name */}
                        <div className="flex-1">
                          <div className="zcwf_col_lab">
                            <label htmlFor="Last_Name" className="text-sm font-medium text-gray-700 mb-1 block">
                              Last Name <span className="text-red-500">*</span>
                            </label>
                          </div>
                          <div className="zcwf_col_fld">
                            <input 
                              type="text" 
                              id="Last_Name" 
                              name="Last Name" 
                              maxLength={80}
                              className="form-input w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Last name"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Company */}
                    <div className="zcwf_row">
                      <div className="zcwf_col_lab">
                        <label htmlFor="Company" className="text-sm font-medium text-gray-700 mb-1 block">
                          Company <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="zcwf_col_fld">
                        <input 
                          type="text" 
                          id="Company" 
                          name="Company" 
                          maxLength={200}
                          className="form-input w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Company name"
                          required
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="zcwf_row">
                      <div className="zcwf_col_lab">
                        <label htmlFor="Email" className="text-sm font-medium text-gray-700 mb-1 block">
                          Email <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="zcwf_col_fld">
                        <input 
                          type="text" 
                          id="Email" 
                          name="Email" 
                          maxLength={100}
                          className="form-input w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Business email"
                          required
                        />
                      </div>
                    </div>

                    {/* Mobile */}
                    <div className="zcwf_row">
                      <div className="zcwf_col_lab">
                        <label htmlFor="Mobile" className="text-sm font-medium text-gray-700 mb-1 block">
                          Mobile
                        </label>
                      </div>
                      <div className="zcwf_col_fld">
                        <input 
                          type="tel" 
                          id="Mobile" 
                          name="Mobile" 
                          maxLength={30}
                          className="form-input w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Phone number"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="zcwf_row">
                      <div className="zcwf_col_fld">
                        <input 
                          type="submit" 
                          id="formsubmit" 
                          className="formsubmit zcwf_button w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-md hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg cursor-pointer"
                          value="Get Free Consultation"
                          name="submit"
                        />
                      </div>
                    </div>
                  </form>

                </div>
              </div>

              {/* Trust Indicators */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    <span>No Spam</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    <span>Free Call</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6 lg:space-y-8">
            <Card>
              <CardContent className="p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-semibold mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Address</div>
                      <div className="text-gray-600 text-sm lg:text-base">Pitampura, New Delhi 110034, Delhi</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Business Hours</div>
                      <div className="text-gray-600 text-sm lg:text-base">Mon - Sat: 9:00 AM - 6:00 PM</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-blue-600 text-white">
              <CardContent className="p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-semibold mb-4">Why Choose Us?</h3>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-white rounded-full flex-shrink-0"></div>
                    <span className="text-sm lg:text-base">Zoho Authorized Partner</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-white rounded-full flex-shrink-0"></div>
                    <span className="text-sm lg:text-base">Free Initial Consultation</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-white rounded-full flex-shrink-0"></div>
                    <span className="text-sm lg:text-base">Quick Response Time</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-white rounded-full flex-shrink-0"></div>
                    <span className="text-sm lg:text-base">Expert Implementation Team</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-white rounded-full flex-shrink-0"></div>
                    <span className="text-sm lg:text-base">Dedicated Support Available</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}